import { useCallback } from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SerializedError } from '@reduxjs/toolkit';
import distributionApi from 'services/api/distribution';
import { ReleaseSongFieldType } from 'services/store/distribution/types';
import { actions } from 'services/store/distribution';
import { selectReleaseSongComposerName } from 'services/store/distribution/selectors';
import { useTextFieldValidation } from 'pages/Distribution/hooks';
import LoadingAdornment from 'pages/Distribution/components/LoadingAdornment';
import useValidationSchema from './useValidationSchema';

type PropsType = {
  releaseId: string;
};

const ComposerName = ({ releaseId }: PropsType) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const composerName = useSelector(selectReleaseSongComposerName(releaseId));
  const { data, loading, error } = composerName;

  const [updateReleaseSong] = distributionApi.useUpdateReleaseSongMutation();

  const { setReleaseSongFieldError } = actions;

  const handleValidationSuccess = useCallback(
    (value: string) => {
      if (data === value) {
        return;
      }
      updateReleaseSong({
        releaseId,
        field: ReleaseSongFieldType.ComposerName,
        value,
      });
    },
    [data, releaseId, updateReleaseSong]
  );

  const handleValidationError = useCallback(
    (validationError: SerializedError | null) => {
      dispatch(
        setReleaseSongFieldError({
          releaseId,
          field: ReleaseSongFieldType.ComposerName,
          error: validationError,
        })
      );
    },
    [releaseId, setReleaseSongFieldError, dispatch]
  );

  const validationSchema = useValidationSchema();

  const { onChange, onBlur } = useTextFieldValidation({
    validator: validationSchema,
    onValidationSuccess: handleValidationSuccess,
    error,
    onValidationError: handleValidationError,
  });

  return (
    <TextField
      fullWidth
      disabled={loading}
      label={t(
        `distribution:tracklist:fields:${ReleaseSongFieldType.ComposerName}:placeholder`
      )}
      defaultValue={data}
      error={!!error}
      helperText={error && error.message}
      InputProps={{
        ...(loading && {
          endAdornment: <LoadingAdornment />,
        }),
      }}
      onChange={onChange}
      onBlur={onBlur}
    />
  );
};

export default ComposerName;
